import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getIngresos,
  crearIngreso,
  actualizarIngreso,
  eliminarIngreso,
} from "../../firebase/ingresos";
import logo from "../../logo.png";

const VERDE = "#1a9e2a";
const AZUL = "#1a3c5e";

const CATEGORIAS = [
  { id: "Venta", color: "#1a3c5e", fondo: "#eff6ff", borde: "#dbeafe" },
  { id: "Alquiler", color: "#15803d", fondo: "#f0fdf4", borde: "#bbf7d0" },
  { id: "Tasacion", color: "#7c3aed", fondo: "#f5f3ff", borde: "#ddd6fe" },
  { id: "Honorarios", color: "#0891b2", fondo: "#ecfeff", borde: "#a5f3fc" },
  { id: "Comision", color: "#ea580c", fondo: "#fff7ed", borde: "#fed7aa" },
  { id: "Intereses", color: "#db2777", fondo: "#fdf2f8", borde: "#fbcfe8" },
  { id: "Otros", color: "#6b7280", fondo: "#f9fafb", borde: "#e5e7eb" },
];

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function fechaJS(fecha) {
  if (!fecha) return null;
  if (typeof fecha.toDate === "function") return fecha.toDate();
  if (fecha instanceof Date) return fecha;
  return new Date(fecha);
}

function formatearMoneda(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  return "$" + Number(n).toLocaleString("es-AR");
}

function formatearFecha(fecha) {
  const d = fechaJS(fecha);
  if (!d) return "";
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function categoriaInfo(id) {
  return CATEGORIAS.find((c) => c.id === id) || CATEGORIAS[CATEGORIAS.length - 1];
}

function deducirCategoria(concepto) {
  const c = (concepto || "").toLowerCase();
  if (c.includes("tasacion") || c.includes("tasación")) return "Tasacion";
  if (c.includes("interes") || c.includes("interés")) return "Intereses";
  if (c.includes("comision") || c.includes("comisión")) return "Comision";
  if (c.includes("venta")) return "Venta";
  if (c.includes("alquiler") || c.includes("locacion") || c.includes("locación")) return "Alquiler";
  if (c.includes("honorario")) return "Honorarios";
  return "Otros";
}

export default function Ingresos() {
  const navigate = useNavigate();
  const [ingresos, setIngresos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [modal, setModal] = useState(null); // null | "nuevo" | objeto ingreso para editar

  async function cargar() {
    setCargando(true);
    const data = await getIngresos();
    setIngresos(data || []);
    setCargando(false);
  }

  useEffect(() => { cargar(); }, []);

  // Anios disponibles en los datos
  const aniosDisponibles = useMemo(() => {
    const set = new Set();
    ingresos.forEach((i) => {
      const d = fechaJS(i.fecha);
      if (d) set.add(d.getFullYear());
    });
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [ingresos]);

  // Ingresos del anio seleccionado
  const delAnio = useMemo(() => {
    return ingresos.filter((i) => {
      const d = fechaJS(i.fecha);
      return d && d.getFullYear() === anioSeleccionado;
    });
  }, [ingresos, anioSeleccionado]);

  // Stats del anio
  const stats = useMemo(() => {
    const total = delAnio.reduce((acc, i) => acc + (Number(i.total) || 0), 0);
    const cant = delAnio.length;
    const mesActual = new Date().getMonth();
    const anioActual = new Date().getFullYear();
    const totalMesActual = ingresos.reduce((acc, i) => {
      const d = fechaJS(i.fecha);
      if (d && d.getFullYear() === anioActual && d.getMonth() === mesActual) {
        return acc + (Number(i.total) || 0);
      }
      return acc;
    }, 0);

    // Cantidad de meses con datos en el anio seleccionado
    const mesesConDatos = new Set();
    delAnio.forEach((i) => {
      const d = fechaJS(i.fecha);
      if (d) mesesConDatos.add(d.getMonth());
    });
    const promedio = mesesConDatos.size > 0 ? total / mesesConDatos.size : 0;

    return { total, cant, totalMesActual, promedio };
  }, [delAnio, ingresos]);

  // Datos por mes para el grafico
  const porMes = useMemo(() => {
    const arr = MESES.map(() => 0);
    delAnio.forEach((i) => {
      const d = fechaJS(i.fecha);
      if (d) arr[d.getMonth()] += Number(i.total) || 0;
    });
    return arr;
  }, [delAnio]);

  const maxMes = useMemo(() => Math.max(...porMes, 1), [porMes]);

  // Top clientes
  const topClientes = useMemo(() => {
    const mapa = {};
    delAnio.forEach((i) => {
      const k = (i.cliente || "Sin cliente").trim();
      if (!mapa[k]) mapa[k] = 0;
      mapa[k] += Number(i.total) || 0;
    });
    return Object.entries(mapa)
      .map(([nombre, total]) => ({ nombre, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [delAnio]);

  // Por categoria
  const porCategoria = useMemo(() => {
    const mapa = {};
    CATEGORIAS.forEach((c) => { mapa[c.id] = 0; });
    delAnio.forEach((i) => {
      const cat = i.categoria || "Otros";
      if (mapa[cat] === undefined) mapa[cat] = 0;
      mapa[cat] += Number(i.total) || 0;
    });
    return Object.entries(mapa)
      .map(([categoria, total]) => ({ categoria, total }))
      .sort((a, b) => b.total - a.total);
  }, [delAnio]);

  // Filtrado para la tabla
  const filtrados = useMemo(() => {
    let lista = [...delAnio];

    if (filtroCategoria) lista = lista.filter((i) => i.categoria === filtroCategoria);
    if (filtroMes !== "") {
      const m = Number(filtroMes);
      lista = lista.filter((i) => {
        const d = fechaJS(i.fecha);
        return d && d.getMonth() === m;
      });
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter((i) =>
        (i.concepto || "").toLowerCase().includes(q) ||
        (i.cliente || "").toLowerCase().includes(q) ||
        (i.colega || "").toLowerCase().includes(q) ||
        (i.notas || "").toLowerCase().includes(q)
      );
    }

    lista.sort((a, b) => {
      const da = fechaJS(a.fecha);
      const db = fechaJS(b.fecha);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.getTime() - da.getTime();
    });

    return lista;
  }, [delAnio, busqueda, filtroCategoria, filtroMes]);

  const totalFiltrado = useMemo(
    () => filtrados.reduce((acc, i) => acc + (Number(i.total) || 0), 0),
    [filtrados]
  );

  async function handleGuardar(datos) {
    if (modal && modal.id) {
      await actualizarIngreso(modal.id, datos);
    } else {
      await crearIngreso(datos);
    }
    setModal(null);
    cargar();
  }

  async function handleEliminar(id) {
    if (!window.confirm("Seguro queres eliminar este ingreso? Esta accion no se puede deshacer.")) return;
    await eliminarIngreso(id);
    setModal(null);
    cargar();
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f4f4f4", fontFamily: "system-ui, sans-serif" }}>

      <nav style={{
        background: "#fff",
        borderBottom: "1px solid #e5e5e5",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        gap: "12px",
      }}>
        <img
          src={logo}
          alt="Confalonieri Propiedades"
          onClick={() => navigate("/admin")}
          style={{ height: "40px", cursor: "pointer" }}
        />
        <button
          onClick={() => navigate("/admin")}
          style={{
            background: "none",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 14px",
            color: "#6b7280",
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: "600",
          }}
        >
          Volver al panel
        </button>
      </nav>

      <div style={{ height: "4px", background: VERDE }} />

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 20px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: AZUL, margin: 0, fontFamily: "Georgia, serif" }}>
              Libro de ingresos
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", margin: "6px 0 0 0" }}>
              Gestion y seguimiento de ingresos de la inmobiliaria
            </p>
          </div>
          <button
            onClick={() => setModal("nuevo")}
            style={{
              background: VERDE,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              padding: "12px 18px",
              fontSize: "14px",
              fontWeight: "700",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(26,158,42,0.25)",
            }}
          >
            + Nuevo ingreso
          </button>
        </div>

        {/* SELECTOR DE ANIO */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
          {aniosDisponibles.map((a) => (
            <button
              key={a}
              onClick={() => setAnioSeleccionado(a)}
              style={{
                background: anioSeleccionado === a ? AZUL : "#fff",
                color: anioSeleccionado === a ? "#fff" : "#374151",
                border: "1px solid " + (anioSeleccionado === a ? AZUL : "#e5e7eb"),
                borderRadius: "10px",
                padding: "8px 18px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              {a}
            </button>
          ))}
        </div>

        {/* STATS */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "14px",
          marginBottom: "24px",
        }}>
          <TarjetaStat label={"Total " + anioSeleccionado} valor={formatearMoneda(stats.total)} color={AZUL} />
          <TarjetaStat label="Promedio mensual" valor={formatearMoneda(stats.promedio)} color={VERDE} />
          <TarjetaStat label="Mes en curso" valor={formatearMoneda(stats.totalMesActual)} color="#7c3aed" />
          <TarjetaStat label="Operaciones" valor={stats.cant.toString()} color="#ea580c" />
        </div>

        {/* GRAFICO POR MES */}
        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "20px", marginBottom: "24px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: "700", color: "#6b7280", margin: "0 0 16px 0", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            Ingresos por mes - {anioSeleccionado}
          </h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "180px", paddingBottom: "30px" }}>
            {porMes.map((monto, i) => {
              const altura = monto > 0 ? Math.max((monto / maxMes) * 150, 4) : 2;
              const esMesActual = i === new Date().getMonth() && anioSeleccionado === new Date().getFullYear();
              return (
                <div
                  key={i}
                  onClick={() => setFiltroMes(filtroMes === String(i) ? "" : String(i))}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", cursor: "pointer", position: "relative" }}
                  title={MESES[i] + ": " + formatearMoneda(monto)}
                >
                  {monto > 0 && (
                    <span style={{
                      fontSize: "9px",
                      color: "#6b7280",
                      fontWeight: "700",
                      whiteSpace: "nowrap",
                      transform: "rotate(-45deg)",
                      transformOrigin: "bottom left",
                      position: "absolute",
                      bottom: altura + 32 + "px",
                      left: "50%",
                    }}>
                      {monto >= 1000000 ? (monto / 1000000).toFixed(1) + "M" : monto >= 1000 ? Math.round(monto / 1000) + "k" : monto}
                    </span>
                  )}
                  <div style={{
                    width: "100%",
                    height: altura + "px",
                    background: filtroMes === String(i) ? AZUL : esMesActual ? VERDE : "#cbd5e1",
                    borderRadius: "6px 6px 0 0",
                    transition: "all 0.2s",
                    minHeight: "2px",
                  }} />
                  <span style={{
                    fontSize: "10px",
                    color: esMesActual ? VERDE : "#6b7280",
                    fontWeight: esMesActual ? "700" : "500",
                    position: "absolute",
                    bottom: "-22px",
                  }}>
                    {MESES[i].substring(0, 3)}
                  </span>
                </div>
              );
            })}
          </div>
          {filtroMes !== "" && (
            <p style={{ margin: "16px 0 0 0", fontSize: "12px", color: "#6b7280", textAlign: "center" }}>
              Filtrando por <strong style={{ color: AZUL }}>{MESES[Number(filtroMes)]}</strong>
              <button onClick={() => setFiltroMes("")} style={{ marginLeft: "8px", background: "none", border: "none", color: VERDE, cursor: "pointer", fontWeight: "700", fontSize: "12px" }}>
                quitar filtro
              </button>
            </p>
          )}
        </div>

        {/* PANELES LATERALES: CATEGORIAS Y TOP CLIENTES */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "14px", marginBottom: "24px" }}>
          <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "20px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: "700", color: "#6b7280", margin: "0 0 14px 0", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Por categoria
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {porCategoria.map((c) => {
                const info = categoriaInfo(c.categoria);
                const pct = stats.total > 0 ? Math.round((c.total / stats.total) * 100) : 0;
                return (
                  <div key={c.categoria}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <span style={{ fontSize: "12px", color: info.color, fontWeight: "700" }}>{c.categoria}</span>
                      <span style={{ fontSize: "12px", color: "#111827", fontWeight: "700" }}>{formatearMoneda(c.total)}</span>
                    </div>
                    <div style={{ height: "6px", background: "#f3f4f6", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ width: pct + "%", height: "100%", background: info.color, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e5e7eb", padding: "20px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: "700", color: "#6b7280", margin: "0 0 14px 0", letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Top clientes
            </h3>
            {topClientes.length === 0 ? (
              <p style={{ fontSize: "13px", color: "#9ca3af", margin: 0 }}>Sin datos en este ano</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {topClientes.map((c, idx) => (
                  <div key={c.nombre} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      background: idx === 0 ? VERDE : idx === 1 ? AZUL : "#9ca3af",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "800",
                      fontSize: "12px",
                      flexShrink: 0,
                    }}>{idx + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#111827", textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.nombre}
                      </p>
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: "700", color: AZUL, flexShrink: 0 }}>
                      {formatearMoneda(c.total)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CONTROLES DE FILTRO */}
        <div style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e5e7eb",
          padding: "14px",
          marginBottom: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por concepto, cliente, colega o notas..."
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
              background: "#f9fafb",
            }}
          />
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={() => setFiltroCategoria("")}
              style={chipStyle(filtroCategoria === "", "#374151")}
            >
              Todas las categorias
            </button>
            {CATEGORIAS.map((c) => (
              <button
                key={c.id}
                onClick={() => setFiltroCategoria(filtroCategoria === c.id ? "" : c.id)}
                style={chipStyle(filtroCategoria === c.id, c.color)}
              >
                {c.id}
              </button>
            ))}
          </div>
        </div>

        {/* TABLA DE INGRESOS */}
        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: "30px" }}>
          <div style={{
            padding: "14px 18px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            background: "#f9fafb",
          }}>
            <h3 style={{ fontSize: "13px", fontWeight: "700", color: "#6b7280", margin: 0, letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Movimientos ({filtrados.length})
            </h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#374151" }}>
              Total filtrado: <strong style={{ color: AZUL, fontSize: "15px" }}>{formatearMoneda(totalFiltrado)}</strong>
            </p>
          </div>

          {cargando ? (
            <p style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: "14px", margin: 0 }}>
              Cargando ingresos...
            </p>
          ) : filtrados.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center" }}>
              <p style={{ color: "#374151", fontSize: "15px", fontWeight: "600", margin: "0 0 6px 0" }}>
                No hay movimientos para mostrar
              </p>
              <p style={{ color: "#9ca3af", fontSize: "13px", margin: "0 0 16px 0" }}>
                {ingresos.length === 0 ? "Cargá el primer ingreso para comenzar" : "Probá ajustar los filtros o el ano seleccionado"}
              </p>
              <button
                onClick={() => setModal("nuevo")}
                style={{
                  background: VERDE,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 18px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                + Nuevo ingreso
              </button>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={thStyle()}>Fecha</th>
                    <th style={thStyle()}>Concepto</th>
                    <th style={thStyle()}>Cliente</th>
                    <th style={thStyle()}>Colega</th>
                    <th style={thStyle()}>Categoria</th>
                    <th style={thStyleRight()}>Ingreso</th>
                    <th style={thStyleRight()}>A cuenta</th>
                    <th style={thStyleRight()}>Menos</th>
                    <th style={thStyleRight()}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((i) => {
                    const info = categoriaInfo(i.categoria);
                    return (
                      <tr
                        key={i.id}
                        onClick={() => setModal(i)}
                        style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td style={tdStyle()}>{formatearFecha(i.fecha)}</td>
                        <td style={tdStyle()}>
                          <p style={{ margin: 0, fontWeight: "600", color: "#111827" }}>{i.concepto || "-"}</p>
                          {i.notas && <p style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#9ca3af" }}>{i.notas}</p>}
                        </td>
                        <td style={tdStyle()}><span style={{ textTransform: "capitalize" }}>{i.cliente || "-"}</span></td>
                        <td style={tdStyle()}><span style={{ textTransform: "capitalize", color: "#6b7280" }}>{i.colega || "-"}</span></td>
                        <td style={tdStyle()}>
                          <span style={{
                            background: info.fondo,
                            color: info.color,
                            border: "1px solid " + info.borde,
                            fontSize: "11px",
                            padding: "3px 8px",
                            borderRadius: "20px",
                            fontWeight: "700",
                            whiteSpace: "nowrap",
                          }}>
                            {i.categoria || "Otros"}
                          </span>
                        </td>
                        <td style={tdStyleRight()}>{i.ingreso ? formatearMoneda(i.ingreso) : "-"}</td>
                        <td style={tdStyleRight()}>{i.aCuenta ? formatearMoneda(i.aCuenta) : "-"}</td>
                        <td style={tdStyleRight()}>{i.menos ? formatearMoneda(i.menos) : "-"}</td>
                        <td style={Object.assign({}, tdStyleRight(), { fontWeight: "700", color: AZUL })}>{formatearMoneda(i.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      <footer style={{
        borderTop: "3px solid " + VERDE,
        padding: "20px",
        textAlign: "center",
        color: "#9ca3af",
        fontSize: "12px",
        background: "#fff",
      }}>
        Confalonieri Propiedades - Libro de ingresos
      </footer>

      {/* MODAL DE FORMULARIO */}
      {modal && (
        <ModalIngreso
          ingreso={modal === "nuevo" ? null : modal}
          onCerrar={() => setModal(null)}
          onGuardar={handleGuardar}
          onEliminar={handleEliminar}
        />
      )}

    </div>
  );
}

function TarjetaStat({ label, valor, color }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "14px",
      padding: "18px 20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <p style={{ color: "#6b7280", fontSize: "11px", margin: 0, fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {label}
      </p>
      <p style={{ fontSize: "22px", fontWeight: "800", color: color, margin: "8px 0 0 0", lineHeight: 1.1, wordBreak: "break-word" }}>
        {valor}
      </p>
    </div>
  );
}

function chipStyle(activo, color) {
  return {
    background: activo ? color : "#fff",
    color: activo ? "#fff" : "#374151",
    border: "1px solid " + (activo ? color : "#e5e7eb"),
    borderRadius: "20px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.15s",
  };
}

function thStyle() {
  return {
    padding: "10px 14px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: "700",
    color: "#6b7280",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
}

function thStyleRight() {
  return Object.assign({}, thStyle(), { textAlign: "right" });
}

function tdStyle() {
  return { padding: "12px 14px", color: "#374151", verticalAlign: "top" };
}

function tdStyleRight() {
  return Object.assign({}, tdStyle(), { textAlign: "right", whiteSpace: "nowrap" });
}

function ModalIngreso({ ingreso, onCerrar, onGuardar, onEliminar }) {
  const [form, setForm] = useState({
    fecha: "",
    concepto: "",
    cliente: "",
    colega: "",
    categoria: "Honorarios",
    ingreso: "",
    aCuenta: "",
    menos: "",
    total: "",
    notas: "",
  });

  useEffect(() => {
    if (ingreso) {
      const d = fechaJS(ingreso.fecha);
      setForm({
        fecha: d ? d.toISOString().substring(0, 10) : "",
        concepto: ingreso.concepto || "",
        cliente: ingreso.cliente || "",
        colega: ingreso.colega || "",
        categoria: ingreso.categoria || "Otros",
        ingreso: ingreso.ingreso || "",
        aCuenta: ingreso.aCuenta || "",
        menos: ingreso.menos || "",
        total: ingreso.total || "",
        notas: ingreso.notas || "",
      });
    } else {
      const hoy = new Date().toISOString().substring(0, 10);
      setForm((f) => Object.assign({}, f, { fecha: hoy }));
    }
  }, [ingreso]);

  function actualizar(campo, valor) {
    setForm((f) => {
      const nuevo = Object.assign({}, f, { [campo]: valor });
      // Auto-deducir categoria al escribir concepto si no hay edicion previa
      if (campo === "concepto" && !ingreso) {
        nuevo.categoria = deducirCategoria(valor);
      }
      // Auto-calcular total si se edita ingreso/aCuenta/menos
      if (campo === "ingreso" || campo === "aCuenta" || campo === "menos") {
        const ing = Number(campo === "ingreso" ? valor : nuevo.ingreso) || 0;
        const cta = Number(campo === "aCuenta" ? valor : nuevo.aCuenta) || 0;
        const men = Number(campo === "menos" ? valor : nuevo.menos) || 0;
        // Si todavia no se toco total manualmente, lo calculamos
        nuevo.total = ing + cta - men;
      }
      return nuevo;
    });
  }

  function submit(e) {
    e.preventDefault();
    if (!form.concepto.trim()) {
      alert("El concepto es obligatorio");
      return;
    }
    onGuardar({
      fecha: form.fecha,
      concepto: form.concepto.trim(),
      cliente: form.cliente.trim(),
      colega: form.colega.trim(),
      categoria: form.categoria,
      ingreso: Number(form.ingreso) || 0,
      aCuenta: Number(form.aCuenta) || 0,
      menos: Number(form.menos) || 0,
      total: Number(form.total) || 0,
      notas: form.notas.trim(),
    });
  }

  return (
    <div
      onClick={onCerrar}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999,
        padding: "20px",
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: "640px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: AZUL, fontFamily: "Georgia, serif" }}>
            {ingreso ? "Editar ingreso" : "Nuevo ingreso"}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            style={{
              background: "none",
              border: "none",
              fontSize: "22px",
              color: "#9ca3af",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >X</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Campo label="Fecha *">
            <input
              type="date"
              value={form.fecha}
              onChange={(e) => actualizar("fecha", e.target.value)}
              required
              style={inputStyle()}
            />
          </Campo>
          <Campo label="Categoria">
            <select
              value={form.categoria}
              onChange={(e) => actualizar("categoria", e.target.value)}
              style={inputStyle()}
            >
              {CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.id}</option>)}
            </select>
          </Campo>
        </div>

        <Campo label="Concepto *">
          <input
            type="text"
            value={form.concepto}
            onChange={(e) => actualizar("concepto", e.target.value)}
            placeholder="ej: honorarios venta casa romeo"
            required
            style={inputStyle()}
          />
        </Campo>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Campo label="Cliente">
            <input
              type="text"
              value={form.cliente}
              onChange={(e) => actualizar("cliente", e.target.value)}
              placeholder="ej: romeo y mariano"
              style={inputStyle()}
            />
          </Campo>
          <Campo label="Colega">
            <input
              type="text"
              value={form.colega}
              onChange={(e) => actualizar("colega", e.target.value)}
              placeholder="ej: juance (opcional)"
              style={inputStyle()}
            />
          </Campo>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          <Campo label="Ingreso">
            <input
              type="number"
              step="0.01"
              value={form.ingreso}
              onChange={(e) => actualizar("ingreso", e.target.value)}
              placeholder="0"
              style={inputStyle()}
            />
          </Campo>
          <Campo label="A cuenta">
            <input
              type="number"
              step="0.01"
              value={form.aCuenta}
              onChange={(e) => actualizar("aCuenta", e.target.value)}
              placeholder="0"
              style={inputStyle()}
            />
          </Campo>
          <Campo label="Menos">
            <input
              type="number"
              step="0.01"
              value={form.menos}
              onChange={(e) => actualizar("menos", e.target.value)}
              placeholder="0"
              style={inputStyle()}
            />
          </Campo>
        </div>

        <Campo label={"Total: " + formatearMoneda(form.total) + "  (se calcula automaticamente)"}>
          <input
            type="number"
            step="0.01"
            value={form.total}
            onChange={(e) => actualizar("total", e.target.value)}
            placeholder="0"
            style={Object.assign({}, inputStyle(), { fontWeight: "700", color: AZUL })}
          />
        </Campo>

        <Campo label="Notas">
          <textarea
            value={form.notas}
            onChange={(e) => actualizar("notas", e.target.value)}
            placeholder="Detalle adicional (opcional)"
            rows={2}
            style={Object.assign({}, inputStyle(), { resize: "vertical", fontFamily: "inherit" })}
          />
        </Campo>

        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
          {ingreso && (
            <button
              type="button"
              onClick={() => onEliminar(ingreso.id)}
              style={{
                background: "#fef2f2",
                color: "#dc2626",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "10px 16px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              Eliminar
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={onCerrar}
              style={{
                background: "#fff",
                color: "#6b7280",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "10px 16px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                background: VERDE,
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 22px",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(26,158,42,0.25)",
              }}
            >
              {ingreso ? "Guardar cambios" : "Crear ingreso"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ marginTop: "12px" }}>
      <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#6b7280", marginBottom: "6px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle() {
  return {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
    background: "#f9fafb",
  };
}
